import React, { ReactElement, useEffect, useState } from 'react';
import { Users } from '@/types/general';
import { useGetInvitationsLimitsQuery } from '@redux/features/users/usersApi';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import BaseTooltip from '@/components/reusables/BaseTooltip';
import { useTranslation } from 'react-i18next';
import { useAbility } from '@/casl.config';
dayjs.extend(duration);

interface ResendInviteProps {
    children?: React.ReactNode;
    row: Users.DTO.CompanyInvitations;
}

export default function ResendInviteTimer({ children, row }: ResendInviteProps) {
    const { t } = useTranslation("", { keyPrefix: "settings.general.companyInformation.tabs.users.inviteUsersTable.table.tooltips" });
    const ability = useAbility();
    const allowedActions = ability.can("user.invite", "general");
    
    const hasOnClick = (child: React.ReactNode): child is ReactElement<{ onClick?: () => void }> => {
        return React.isValidElement(child) && typeof child.props.onClick === 'function';
    };

    const { last_sent_at } = row;
    
    const { data: invitationsLimit } = useGetInvitationsLimitsQuery();

    const [showTimer, setShowTimer] = useState(false);
    const [timeLeft, setTimeLeft] = useState<string | null>(null);
    // let timerInterval: NodeJS.Timeout | null = null;
    const [isHovering, setIsHovering] = useState(false);

    useEffect(() => {
        let timerInterval: NodeJS.Timeout | undefined;

        if (isHovering && last_sent_at && invitationsLimit?.resend_timeout) {
            const expiryTime = dayjs(last_sent_at).add(invitationsLimit?.resend_timeout, 'second');
            const now = dayjs();
            const difference = expiryTime.diff(now, 'second');

            if (difference > 0) {
                setShowTimer(true);
                timerInterval = setInterval(() => {
                    const currentDifference = expiryTime.diff(dayjs(), 'second');
                    if (currentDifference > 0) {
                        setTimeLeft(dayjs.duration(currentDifference, 'seconds').format('mm:ss'));
                    } else {
                        setShowTimer(false);
                        setTimeLeft(null);
                        clearInterval(timerInterval);
                        timerInterval = undefined;
                    }
                }, 0);
            }
        } else {
            setShowTimer(false);
            setTimeLeft(null);
            if (timerInterval) {
                clearInterval(timerInterval);
                timerInterval = undefined;
            }
        }

        return () => {
            if (timerInterval) {
                clearInterval(timerInterval);
                timerInterval = undefined;
            }
        };
    }, [isHovering, last_sent_at, invitationsLimit?.resend_timeout]);

    const handleMouseEnter = () => {
        setIsHovering(true);
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
    };

    const hasResendTimeoutExpired = last_sent_at && invitationsLimit?.resend_timeout
        ? dayjs(last_sent_at).add(invitationsLimit.resend_timeout, 'second').isBefore(dayjs())
        : false;

    const renderChildren = () => {
        if (React.Children.only(children) && hasOnClick(children)) {
            return React.cloneElement(children, {
                onClick: showTimer ? undefined : children.props.onClick,
            });
        }
        return children;
    };

    return (
        <BaseTooltip active={allowedActions} title={hasResendTimeoutExpired ? t("resendInvite") as string : t("resendInviteDisabled") as string} placement="bottom-end">
            {
                !hasResendTimeoutExpired ?  (
                    <div
                    className="flex p-2 items-center rounded-xl hover:bg-indigo-100 cursor-pointer"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {isHovering && showTimer && timeLeft && (
                        <div className="w-9 text-sm text-gray-500">{`${timeLeft}`}</div>
                        
                    )}
                    <div className='ml-2'>{renderChildren()}</div>
                </div>
                ) : (
                    <div className='p-2 hover:text-tables-highlightedIcon'>{renderChildren()}</div>
                )
            }
        </BaseTooltip>
    )
}
